import React, { Context, ReactNode, useContext, useRef, useState } from "react";
import { drawDynamicComponent } from "./_drawDynamicComponent";
import { DynamicComponent } from "../core/_models";
import { useDynamicForm } from "../core/DynamicForm";
import { Modal } from "react-bootstrap";
import { ModalDrawer } from "./ModalDrawer";
import { ButtonComponent } from "./building-blocks/ButtonComponent";
import BlockUi from "react-block-ui";
import { SubmittableFormComponent } from "./building-blocks/SubmittableFormComponent";

type Props = {
  children?: ReactNode
  config: any
  isModal?: boolean
  context: Context<any>
}

type ThisFormContextProps = {
  set: (param: string, value: any) => void
  values: { [key: string]: any }
  submit: (payload: any, modal: any) => void
}

export function FormDrawer({ config, context, isModal = false }: Props) {
  const { modal, okButton, cancelButton, caption, children } = config;
  const { submitToFormData, loading: formDataLoading } = useDynamicForm();

  const parentContext = useContext(context);

  // Collected field values for this form. Kept in a ref so that writes from child
  // components (via `set`) do not trigger a re-render — the bag is only read at
  // submit time, never rendered.
  const valuesRef = useRef<{ [key: string]: any }>({});

  // A stable context object for this form instance. Creating it once (instead of
  // on every render, as before) prevents the whole subtree from remounting on
  // every incidental re-render, which previously dropped focus and child state.
  const [thisFormContext] = useState(() =>
    React.createContext<ThisFormContextProps | null>(null)
  );

  // Reset the form and force a remount of its fields only when fresh data arrives
  // from the server (a new `config` object). This preserves the previous
  // "rebuild the form on server response" behaviour — child fields re-read their
  // initial value on mount — without recreating the context on every render.
  const prevConfigRef = useRef(config);
  const versionRef = useRef(0);
  if (prevConfigRef.current !== config) {
    prevConfigRef.current = config;
    versionRef.current += 1;
    valuesRef.current = {};
  }
  const contentKey = versionRef.current;

  const values = valuesRef.current;

  const submit = async ({ action, values: additionalValues = {} }: { action: any, values: any }, modal: any) => {
    if (parentContext === null) {
      submitToFormData({
        action: modal !== undefined ? undefined : action,
        modal,
        values: {
          ...values,
          ...additionalValues
        },
        formId: config.id
      });
    } else {
      parentContext.submit(action, {
        action,
        values: {
          ...values,
          ...additionalValues
        },
        formId: config.id,
        modal
      });
    }
  };

  const contextValue: ThisFormContextProps = {
    set: (param, value) => {
      values[param] = value;
    },
    values,
    submit
  };

  if (isModal) {

    const modalForm = (<>
      <div className="modal-body">
        {config.children !== undefined && (config.children as any[]).map(childConfig => drawDynamicComponent(childConfig, thisFormContext))}

        {modal != null &&
          <ModalDrawer config={modal} context={thisFormContext} inner={true} />
        }
      </div>
      <div className="modal-footer d-flex justify-content-around">
        <span />
        {(okButton != null || cancelButton != null) && <>
          {cancelButton != null &&
            <ButtonComponent config={cancelButton} context={thisFormContext} color="error" />}
          {okButton != null &&
            <ButtonComponent
              config={okButton}
              context={thisFormContext}
              className="ms-4"
              color="success"
              isSubmit
            />}
        </>}
        <span />
      </div>
    </>);

    return (
      <thisFormContext.Provider key={contentKey} value={contextValue}>
        <Modal
          className="modal-sticky modal-sticky-lg modal-sticky-bottom-right"
          id="kt_main_modal"
          role="dialog"
          data-backdrop="true"
          aria-hidden="true"
          tabIndex="-1"
          show={true}
          animation={false}
          size="lg"
        >
          <div className="modal-content shadow-lg">
            <div className="modal-header">
              <h5 className="modal-title">{caption || "Dialog"}</h5>
            </div>
            <BlockUi blocking={formDataLoading}>
              {okButton != null && (
                <SubmittableFormComponent config={okButton} context={thisFormContext}>
                  {modalForm}
                </SubmittableFormComponent>
              )}
              {okButton == null && modalForm}
            </BlockUi>
          </div>
        </Modal>
      </thisFormContext.Provider>
    );
  }

  return (
    <thisFormContext.Provider key={contentKey} value={contextValue}>
      {children !== undefined && (children as DynamicComponent[]).map(childConfig => drawDynamicComponent(childConfig, thisFormContext))}

      {modal != null && <ModalDrawer config={modal} context={thisFormContext} />}
    </thisFormContext.Provider>
  );
}
